import { useState } from 'react';
import type { ReactNode } from 'react';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import loadable from '@loadable/component';

import { useTheme, Grid } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import FvOutline from '@simplywallst/ui-core/icons/FvOutline';

import type { Holding } from '@/hooks/portfolio/usePortfolio';
import { useIsMobileScreen } from '@/hooks/useMedia';

import {
  StyledTable,
  TableHeadRow,
  TableHeadCell,
  ScrollPane,
  FairValueIcon,
} from './styled';

const FairValueTooltip = loadable(() => import('../FairValueTooltip'), {
  resolveComponent: (c) => c.FairValueTooltip,
});

interface Props {
  children: ReactNode;
  holdingId?: Holding['id'] | null;
}

const TableContainer = ({ children }: { children: ReactNode }) => {
  return (
    <ScrollPane>
      <StyledTable>{children}</StyledTable>
    </ScrollPane>
  );
};

export const Table = ({ holdingId, children }: Props) => {
  const theme = useTheme();
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [isSticky, setIsSticky] = useState(false);

  return (
    <TableContainer>
      <thead data-cy-id="holdings-table-head">
        <TableHeadRow>
          <IntersectionObserver
            threshold={[1]}
            onChange={({ intersectionRatio }) => {
              setIsSticky(intersectionRatio < 1);
            }}
          >
            <TableHeadCell isSticky={isSticky}>Symbol</TableHeadCell>
          </IntersectionObserver>
          <>
            <TableHeadCell>Last Price</TableHeadCell>
            <TableHeadCell>
              <Grid
                templateColumns="min-content min-content min-content"
                alignItems="center"
                gap="4px"
              >
                <FairValueIcon>
                  <FvOutline fill={theme.color.whiteRGBA(0.7)} />
                </FairValueIcon>
                Fair Value
                <FairValueTooltip />
              </Grid>
            </TableHeadCell>
          </>

          <TableHeadCell>7D Return</TableHeadCell>
          {!isMobileScreen && holdingId ? (
            <>
              <TableHeadCell>Total Return</TableHeadCell>
              <TableHeadCell>Value & Cost</TableHeadCell>
            </>
          ) : (
            <>
              <TableHeadCell>Total Return</TableHeadCell>
              <TableHeadCell>Value & Cost</TableHeadCell>
              <TableHeadCell>Weight & Shares</TableHeadCell>
              <TableHeadCell>Avg. Price</TableHeadCell>
              <TableHeadCell>1Y chart</TableHeadCell>
            </>
          )}
          <TableHeadCell />
        </TableHeadRow>
      </thead>
      <tbody data-cy-id="holdings-table-body">{children}</tbody>
    </TableContainer>
  );
};

export const DemoTable = ({ holdingId, children }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [isSticky, setIsSticky] = useState(false);

  return (
    <TableContainer>
      <thead data-cy-id="holdings-table-head">
        <TableHeadRow>
          <IntersectionObserver
            threshold={[1]}
            onChange={({ intersectionRatio }) => {
              setIsSticky(intersectionRatio < 1);
            }}
          >
            <TableHeadCell isSticky={isSticky}>Symbol</TableHeadCell>
          </IntersectionObserver>
          <TableHeadCell>
            <Grid
              templateColumns="max-content 24px"
              alignItems="center"
              gap="4px"
            >
              Price Vs Fair Value
              <FairValueTooltip />
            </Grid>
          </TableHeadCell>
          <TableHeadCell>7D Return</TableHeadCell>
          {!isMobileScreen && holdingId ? (
            <>
              <TableHeadCell>Total Return</TableHeadCell>
              <TableHeadCell>Value & Cost</TableHeadCell>
              <TableHeadCell>1Y chart</TableHeadCell>
            </>
          ) : (
            <>
              <TableHeadCell>Total Return</TableHeadCell>
              <TableHeadCell>Value & Cost</TableHeadCell>
              <TableHeadCell>Weight & Shares</TableHeadCell>
              <TableHeadCell>Avg. Price</TableHeadCell>
              <TableHeadCell>1Y chart</TableHeadCell>
            </>
          )}
          <TableHeadCell />
        </TableHeadRow>
      </thead>
      <tbody data-cy-id="holdings-table-body">{children}</tbody>
    </TableContainer>
  );
};
